import { defineComponent as _defineComponent } from 'vue'
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import {
  computed,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';
import { useStore } from 'vue2-helpers/vuex';
import LRPopup from '@/components/login-register/LRPopup.vue';
import useVuelidate from '@vuelidate/core';
import useBCValidators from '@/mixins/useBCValidators';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import BCXVuelidateErrors from '@/components/molecules/BCXVuelidateErrors.vue';
import ButtonBlock from '@/components/login-register/ButtonBlock.vue';
import useI18n from '@/mixins/useI18n';
import ByteCookieLogoFull from '@/components/svg/logo/logo-full-for-darkBG.svg?inline';
import { useRoute } from 'vue2-helpers/vue-router';
import InviteCodePopup from '@/components/login-register/InviteCodePopup.vue';
import CompanyInviteCodePopup from '@/components/login-register/CompanyInviteCodePopup.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import BackendErrors from '@/components/login-register/BackendErrors.vue';
import { TranslatedErrorCode } from '@/models/BackendError';
import { Lang, User, UserType } from '@/models/User';
import clearUserLocalStorage from '@/utils/clearUserLocalStorage';
// eslint-disable-next-line import/named
import { LegalItem } from '@/models/Legal';
import RegistrationService, { RegisterBasicDataRequest } from '@/services/RegistrationService';
import { useLocalStorage, useSessionStorage } from '@vueuse/core';
import BCXRadio from '@/components/molecules/forms/BCXRadio.vue';
import router from '@/router';
import { logDebug } from '@/utils/logger';


export default /*#__PURE__*/_defineComponent({
  __name: 'Registration',
  setup(__props) {

const { tUserTypeFallBack, locale } = useI18n();
const store = useStore();
const { emailValidation, required, termsValidation } = useBCValidators();
const { isSmartphoneWidth } = useResponsiveness();
const route = useRoute();
const storedUser = useLocalStorage('user', null);
const userFormFields = useSessionStorage<any>('userFormFields', null);
const invitingCompanyName = ref('');
const fields = reactive({
  firstname: '',
  lastname: '',
  companyName: '',
  email: '',
  code: '',
  checked: false
});

const accountType = computed(() => {
  switch (route.name) {
    case 'register-guest':
      return UserType.GUEST;
    case 'register-customer':
      return UserType.COMPANY;
    default:
      return UserType.FREELANCER;
  }
});
const selectedAccountType = ref<UserType>(accountType.value);
const updateRoute = (value: UserType) => {
  console.debug('Updating account type to', value);
  selectedAccountType.value = value;
};
watch(
  () => selectedAccountType.value,
  () => {
    const routePath = selectedAccountType.value === UserType.COMPANY ? '/register-customer' : '/register';
    if (route.path !== routePath) {
      router.replace({
        path: routePath,
        query: {
          ...fields,
          checked: String(fields.checked),
          companyName: undefined
        }
      });
    } else {
      logDebug('Route already matches', routePath);
    }
  }
);
const isCompanyType = computed(() => accountType.value === UserType.COMPANY);
const isGuestType = computed(() => accountType.value === UserType.GUEST);
const isFreelancerType = computed(() => accountType.value === UserType.FREELANCER);

const t = (key: string, ...args: any) => tUserTypeFallBack(key, accountType.value, ...args);

const titleParts = computed(() => (t('registration_USERTYPE.title') as string).split(/ByteCookie/));

const rules = computed(() => {
  if (isCompanyType.value) {
    return {
      firstname: { required },
      lastname: { required },
      email: emailValidation,
      companyName: { required },
      checked: termsValidation,
      code: {}
    };
  }
  if (isFreelancerType.value) {
    return {
      firstname: { required },
      lastname: { required },
      email: emailValidation,
      checked: termsValidation,
      code: { required }
    };
  }
  if (isGuestType.value) {
    return {
      firstname: { required },
      lastname: { required }
    };
  }
  return null;
});

const v = useVuelidate(rules, fields);

const checkedTermsAndPrivacy = ref(false);
const submitting = ref(false);
const legalText = ref<LegalItem | false>(false);
const isInvitePopupVisible = ref(false);
const isCompanyInvitePopupVisible = ref(false);
const backendErrors = ref<TranslatedErrorCode[] | string[]>([]);
const userId = ref('');

const prefillForm = async () => {
  const isGuest = isGuestType.value;
  const previousFormFields = JSON.parse(userFormFields.value ?? '{}');
  const { inviteCode } = store.getters;

  if (isGuest) {
    const guest = await RegistrationService.getRegisteringCompanyGuest(route.params?.inviteId);
    const {
      firstName, lastName, companyName, inviteId
    } = guest;
    fields.firstname = firstName ?? '';
    fields.lastname = lastName ?? '';
    fields.code = inviteId;
    invitingCompanyName.value = companyName;
  } else {
    const pid = store.getters.registrationProcessId;
    const registeringUser = pid ? await RegistrationService.getRegisteringUser(pid).catch(() => null) : null;
    const user = registeringUser ?? (store.getters?.user as User) ?? storedUser.value;
    if (user) {
      fields.companyName = user.company?.name ?? previousFormFields?.companyName ?? '';
      fields.firstname = user.firstname ?? '';
      fields.lastname = user.lastname ?? '';
      fields.email = user.email ?? '';
      userId.value = user.userId ?? '';
    } else {
      fields.companyName = (route?.query?.companyName as string) ?? '';
      fields.email = (route?.query?.email as string) ?? '';
      fields.firstname = (route?.query?.firstname as string) ?? '';
      fields.lastname = (route?.query?.lastname as string) ?? '';
    }
    fields.code = route?.query?.code || inviteCode || '';
  }
};

onMounted(() => {
  const { inviteCode } = store.getters;

  prefillForm();
  if (isGuestType.value) {
    store.state.registration.registrationProcessId = undefined;
    localStorage.removeItem('registration_process_id');
  }

  if (inviteCode) {
    checkedTermsAndPrivacy.value = true;
  } else if (route?.params?.showInvitePopup) {
    isInvitePopupVisible.value = true;
  }
  clearUserLocalStorage();
});

const submit = async () => {
  const isValid = await v.value.$validate();
  if (!submitting.value && isValid) {
    const {
      firstname, lastname, companyName, email
    } = fields;

    let localeSetting = Lang.de;
    if (locale.value === 'en') {
      localeSetting = Lang.en;
    }

    let inviteCode: string | undefined = fields.code?.replace(/\s/g, '');
    if (!inviteCode?.length) {
      inviteCode = undefined;
    }

    const params: RegisterBasicDataRequest = {
      userId: '',
      firstname,
      lastname,
      locale: localeSetting,

      ...(isGuestType.value
        ? {
          companyInviteId: route.params?.inviteId,
          role: 'COMPANY_GUEST',
          checkedAgbAndPrivacy: true
        }
        : {
          email,
          inviteCode,
          checkedAgbAndPrivacy: fields.checked
        }),
      ...(isCompanyType.value && {
        companyName,
        role: 'COMPANY_ROOT'
      })
    };

    submitting.value = true;
    userFormFields.value = JSON.stringify(fields);
    const backendErrorsObject: TranslatedBackendErrors = await store.dispatch('submitBasicData', params);
    backendErrors.value = backendErrorsObject.allErrors;
    submitting.value = false;
  }
};

const onShow = (which: 'terms' | 'privacy') => {
  legalText.value = which;
};

const showHelpPopup = () => {
  if (isCompanyType.value) {
    isCompanyInvitePopupVisible.value = true;
  } else {
    isInvitePopupVisible.value = true;
  }
};

const showLoginWithEmail = computed(() => backendErrors.value.includes(TranslatedErrorCode.EMAIL_NOT_UNIQUE));
const loginLinkText = computed(() => {
  if (locale.value === 'de') {
    return (t('buttons.login') as string).toLowerCase();
  }
  return t('buttons.login');
});

return { __sfc: true,tUserTypeFallBack, locale, store, emailValidation, required, termsValidation, isSmartphoneWidth, route, storedUser, userFormFields, invitingCompanyName, fields, accountType, selectedAccountType, updateRoute, isCompanyType, isGuestType, isFreelancerType, t, titleParts, rules, v, checkedTermsAndPrivacy, submitting, legalText, isInvitePopupVisible, isCompanyInvitePopupVisible, backendErrors, userId, prefillForm, submit, onShow, showHelpPopup, showLoginWithEmail, loginLinkText, BCXMarkdown, BCXValidatedInput, LRPopup, BCXCheckbox, BCXVuelidateErrors, ButtonBlock, ByteCookieLogoFull, InviteCodePopup, CompanyInviteCodePopup, BackendErrors, UserType, BCXRadio }
}

})